'use client';

import { c as _c } from "react/compiler-runtime";
import { FeatherAlertTriangle, FeatherCheck, FeatherDownload, FeatherFileText } from '@subframe/core';
import type { ArquivoDoPacote, FormatoDownload, PacoteDosAutos, PortaDosAutos, RegistroDeAnexos } from '../../types/baixarProcesso.types';
import { entregaEmPartes, formatarBytes, LIMITE_POR_PARTE_MB, nomeDaParte, nomeDoPdf, paginasDaParte, partesEmOrdem, prontoSemPartes, resumoDoPacote } from '../../utils/arquivosDoPacote';
import { totalForaDoPdf } from '../../utils/anexosDoRegistro';
import { AnexosForaDoPdf } from './AnexosForaDoPdf';
import { FalhaAoCarregarRegistro } from './FalhaAoCarregarRegistro';
import { RegistroDeAnexosDoPacote } from './RegistroDeAnexosDoPacote';
interface EtapaPacoteProntoProps {
  formato: FormatoDownload;
  numeroProcesso: string;
  pacote: PacoteDosAutos;
  /** Nada foi montado: o servidor devolveu o pacote que a versão atual do processo já tinha. */
  reaproveitado: boolean;
  arquivoEmPreparo: ArquivoDoPacote | null;
  onBaixarArquivo: (arquivo: ArquivoDoPacote) => void;
  porta: PortaDosAutos;
  /** O registro de anexos do pacote, quando a tela o buscou (ver `precisaDoRegistro`). */
  registro: RegistroDeAnexos | null;
  carregandoRegistro: boolean;
  falhaNoRegistro: boolean;
  enviandoCiencia: boolean;
  onDarCiencia: () => void;
  onRecarregarRegistro: () => void;
}
interface LinhaDoArquivo {
  arquivo: ArquivoDoPacote;
  nome: string;
  detalhe: string;
  tamanhoBytes: number | null;
  /** Texto, e não só cor: a parte passou do limite porque uma página sozinha passa dele. */
  alerta: string | null;
}
function linhasDoPacote(formato: FormatoDownload, pacote: PacoteDosAutos, numeroProcesso: string): LinhaDoArquivo[] {
  if (!entregaEmPartes(formato, pacote)) {
    const paginas = pacote.totalPaginas !== null ? `${pacote.totalPaginas} páginas · ` : '';
    return [{
      arquivo: 'pdf',
      nome: nomeDoPdf(numeroProcesso),
      detalhe: `${paginas}código de validação ${pacote.codigoAutenticacao}`,
      tamanhoBytes: pacote.tamanhoBytes,
      alerta: null
    }];
  }
  const partes = partesEmOrdem(pacote);
  return partes.map(parte => ({
    arquivo: `parte-${parte.numero}`,
    nome: nomeDaParte(numeroProcesso, parte.numero, partes.length),
    detalhe: paginasDaParte(parte),
    tamanhoBytes: parte.tamanhoBytes,
    alerta: parte.excedeLimite ? `Passa de ${LIMITE_POR_PARTE_MB} MB: uma única página já excede o limite.` : null
  }));
}
export function EtapaPacotePronto(t0) {
  const $ = _c(89);
  const {
    formato,
    numeroProcesso,
    pacote,
    reaproveitado,
    arquivoEmPreparo,
    onBaixarArquivo,
    porta,
    registro,
    carregandoRegistro,
    falhaNoRegistro,
    enviandoCiencia,
    onDarCiencia,
    onRecarregarRegistro
  } = t0;
  let t1;
  if ($[0] !== formato || $[1] !== pacote) {
    t1 = entregaEmPartes(formato, pacote);
    $[0] = formato;
    $[1] = pacote;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const emPartes = t1;
  let mostraAnexosFora;
  let t10;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[3] !== arquivoEmPreparo || $[4] !== carregandoRegistro || $[5] !== emPartes || $[6] !== enviandoCiencia || $[7] !== falhaNoRegistro || $[8] !== formato || $[9] !== numeroProcesso || $[10] !== onBaixarArquivo || $[11] !== onDarCiencia || $[12] !== onRecarregarRegistro || $[13] !== pacote || $[14] !== reaproveitado || $[15] !== registro) {
    const linhas = linhasDoPacote(formato, pacote, numeroProcesso);
    let t11;
    if ($[26] !== formato || $[27] !== pacote) {
      t11 = formato === "partes" && prontoSemPartes(pacote);
      $[26] = formato;
      $[27] = pacote;
      $[28] = t11;
    } else {
      t11 = $[28];
    }
    const partesFaltando = t11;
    let t12;
    if ($[29] !== pacote || $[30] !== registro) {
      t12 = totalForaDoPdf(pacote, registro);
      $[29] = pacote;
      $[30] = registro;
      $[31] = t12;
    } else {
      t12 = $[31];
    }
    const fora = t12;
    mostraAnexosFora = pacote.cienciaPendente || (fora ?? 0) > 0;
    const downloadTravado = arquivoEmPreparo !== null || pacote.cienciaPendente;
    t7 = "flex min-h-0 w-full flex-1 flex-col gap-[18px] overflow-y-auto px-6 pt-1 pb-6";
    let t13;
    if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = <span className="bg-success-600 mt-px flex h-5 w-5 flex-none items-center justify-center rounded-full text-white"><FeatherCheck className="h-[13px] w-[13px]" /></span>;
      $[32] = t13;
    } else {
      t13 = $[32];
    }
    let t14;
    if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = <span className="text-success-800 text-[13.5px] font-semibold">Pacote pronto para baixar</span>;
      $[33] = t14;
    } else {
      t14 = $[33];
    }
    let t15;
    if ($[34] !== formato || $[35] !== pacote) {
      t15 = resumoDoPacote(formato, pacote);
      $[34] = formato;
      $[35] = pacote;
      $[36] = t15;
    } else {
      t15 = $[36];
    }
    let t16;
    if ($[37] !== t15) {
      t16 = <span className="text-success-700 text-[12.5px] leading-[1.6]">{t15}</span>;
      $[37] = t15;
      $[38] = t16;
    } else {
      t16 = $[38];
    }
    let t17;
    if ($[39] !== reaproveitado) {
      t17 = reaproveitado ? <span className="text-success-700 mt-1 text-[12px] leading-[1.6]">Reaproveitado da geração anterior — o processo não teve movimentação desde então, então o conteúdo é o mesmo e nada foi montado de novo.</span> : null;
      $[39] = reaproveitado;
      $[40] = t17;
    } else {
      t17 = $[40];
    }
    if ($[41] !== t16 || $[42] !== t17) {
      t8 = <div className="border-success-200 bg-success-50 flex items-start gap-3 rounded-[12px] border border-solid px-[18px] py-4">{t13}<div className="flex flex-col gap-0.5">{t14}{t16}{t17}</div></div>;
      $[41] = t16;
      $[42] = t17;
      $[43] = t8;
    } else {
      t8 = $[43];
    }
    if ($[44] !== partesFaltando) {
      t9 = partesFaltando ? <div className="border-warning-200 bg-warning-50 flex items-start gap-3 rounded-[12px] border border-solid px-[18px] py-3" role="status"><FeatherAlertTriangle className="text-warning-700 mt-px h-[17px] w-[17px] flex-none" /><span className="text-warning-800 text-[12.5px] leading-[1.6]">Este pacote foi gerado antes da divisão em partes, então não tem o ZIP. O PDF completo abaixo tem o mesmo conteúdo; para as partes de até {LIMITE_POR_PARTE_MB} MB, peça o ZIP de novo.</span></div> : null;
      $[44] = partesFaltando;
      $[45] = t9;
    } else {
      t9 = $[45];
    }
    if ($[46] !== carregandoRegistro || $[47] !== enviandoCiencia || $[48] !== falhaNoRegistro || $[49] !== fora || $[50] !== mostraAnexosFora || $[51] !== onDarCiencia || $[52] !== onRecarregarRegistro || $[53] !== pacote.cienciaPendente || $[54] !== registro) {
      t10 = mostraAnexosFora ? <AnexosForaDoPdf carregando={carregandoRegistro} cienciaPendente={pacote.cienciaPendente} enviandoCiencia={enviandoCiencia} falhou={falhaNoRegistro} onDarCiencia={onDarCiencia} onTentarDeNovo={onRecarregarRegistro} registro={registro} total={fora} /> : null;
      $[46] = carregandoRegistro;
      $[47] = enviandoCiencia;
      $[48] = falhaNoRegistro;
      $[49] = fora;
      $[50] = mostraAnexosFora;
      $[51] = onDarCiencia;
      $[52] = onRecarregarRegistro;
      $[53] = pacote.cienciaPendente;
      $[54] = registro;
      $[55] = t10;
    } else {
      t10 = $[55];
    }
    t5 = "flex flex-col gap-2";
    const t18 = emPartes ? "Partes do pacote" : "Arquivo gerado";
    if ($[56] !== t18) {
      t6 = <span className="text-[12px] font-medium text-neutral-600" id="arquivos-do-pacote">{t18}</span>;
      $[56] = t18;
      $[57] = t6;
    } else {
      t6 = $[57];
    }
    t2 = "arquivos-do-pacote";
    t3 = "m-0 list-none overflow-hidden rounded-[8px] border border-solid border-neutral-200 p-0";
    let t19;
    if ($[58] !== arquivoEmPreparo || $[59] !== downloadTravado || $[60] !== onBaixarArquivo) {
      t19 = linha => <li className="flex items-center gap-3 border-t border-solid border-neutral-100 px-[14px] py-[11px] first:border-t-0" key={linha.arquivo}><FeatherFileText className="h-4 w-4 flex-none text-neutral-500" /><div className="flex min-w-0 flex-1 flex-col gap-px"><span className="text-default-font text-[13px] font-medium">{linha.nome}</span><span className="text-[11.5px] text-neutral-500">{linha.detalhe}</span>{linha.alerta ? <span className="text-warning-700 text-[11.5px]">{linha.alerta}</span> : null}</div>{linha.tamanhoBytes !== null ? <span className="flex-none text-[12px] whitespace-nowrap text-neutral-600 tabular-nums">{formatarBytes(linha.tamanhoBytes)}</span> : null}<button aria-busy={arquivoEmPreparo === linha.arquivo} aria-label={`Baixar ${linha.nome}`} className="flex h-7 w-7 flex-none items-center justify-center rounded-[8px] border border-solid border-neutral-200 text-neutral-600 hover:bg-neutral-50 disabled:cursor-default disabled:opacity-60" disabled={downloadTravado} onClick={() => onBaixarArquivo(linha.arquivo)} type="button"><FeatherDownload className="h-3.5 w-3.5" /></button></li>;
      $[58] = arquivoEmPreparo;
      $[59] = downloadTravado;
      $[60] = onBaixarArquivo;
      $[61] = t19;
    } else {
      t19 = $[61];
    }
    t4 = linhas.map(t19);
    $[3] = arquivoEmPreparo;
    $[4] = carregandoRegistro;
    $[5] = emPartes;
    $[6] = enviandoCiencia;
    $[7] = falhaNoRegistro;
    $[8] = formato;
    $[9] = numeroProcesso;
    $[10] = onBaixarArquivo;
    $[11] = onDarCiencia;
    $[12] = onRecarregarRegistro;
    $[13] = pacote;
    $[14] = reaproveitado;
    $[15] = registro;
    $[16] = mostraAnexosFora;
    $[17] = t10;
    $[18] = t2;
    $[19] = t3;
    $[20] = t4;
    $[21] = t5;
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
  } else {
    mostraAnexosFora = $[16];
    t10 = $[17];
    t2 = $[18];
    t3 = $[19];
    t4 = $[20];
    t5 = $[21];
    t6 = $[22];
    t7 = $[23];
    t8 = $[24];
    t9 = $[25];
  }
  let t11;
  if ($[62] !== t2 || $[63] !== t3 || $[64] !== t4) {
    t11 = <ol aria-labelledby={t2} className={t3}>{t4}</ol>;
    $[62] = t2;
    $[63] = t3;
    $[64] = t4;
    $[65] = t11;
  } else {
    t11 = $[65];
  }
  let t12;
  if ($[66] !== t11 || $[67] !== t5 || $[68] !== t6) {
    t12 = <div className={t5}>{t6}{t11}</div>;
    $[66] = t11;
    $[67] = t5;
    $[68] = t6;
    $[69] = t12;
  } else {
    t12 = $[69];
  }
  let t13;
  if ($[70] !== porta || $[71] !== registro) {
    t13 = porta === "secretaria" && registro ? <RegistroDeAnexosDoPacote registro={registro} /> : null;
    $[70] = porta;
    $[71] = registro;
    $[72] = t13;
  } else {
    t13 = $[72];
  }
  let t14;
  if ($[73] !== carregandoRegistro || $[74] !== falhaNoRegistro || $[75] !== mostraAnexosFora || $[76] !== onRecarregarRegistro || $[77] !== porta || $[78] !== registro) {
    t14 = porta === "secretaria" && !registro && !mostraAnexosFora && falhaNoRegistro && !carregandoRegistro ? <FalhaAoCarregarRegistro mensagem={"N\xE3o conseguimos carregar o registro de anexos do pacote."} onTentarDeNovo={onRecarregarRegistro} /> : null;
    $[73] = carregandoRegistro;
    $[74] = falhaNoRegistro;
    $[75] = mostraAnexosFora;
    $[76] = onRecarregarRegistro;
    $[77] = porta;
    $[78] = registro;
    $[79] = t14;
  } else {
    t14 = $[79];
  }
  let t15;
  if ($[80] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <p className="text-[12px] leading-[1.6] text-neutral-500">O pacote fica disponível permanentemente nesta tela e pelo link do e-mail de aviso. O conteúdo é congelado no momento da geração — movimentações posteriores exigem um novo pacote.</p>;
    $[80] = t15;
  } else {
    t15 = $[80];
  }
  let t16;
  if ($[81] !== t10 || $[82] !== t12 || $[83] !== t13 || $[84] !== t14 || $[85] !== t7 || $[86] !== t8 || $[87] !== t9) {
    t16 = <div className={t7}>{t8}{t9}{t10}{t12}{t13}{t14}{t15}</div>;
    $[81] = t10;
    $[82] = t12;
    $[83] = t13;
    $[84] = t14;
    $[85] = t7;
    $[86] = t8;
    $[87] = t9;
    $[88] = t16;
  } else {
    t16 = $[88];
  }
  return t16;
}